import React, { useEffect, useMemo, useRef } from 'react'
import { TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setState, setTotalValues } from "./defineSlice";
import {
	removeComma,
	rowDepletionModelKey,
	splitQuotaAcrossWeeks,
	findGridRowByCustomerSku,
	commitGridDataUpdate,
	PRE_ROUNDER_BACKUP_SYNC_FIELDS,
} from "./defineUtils";

import debounce from 'lodash/debounce';
import {
	QUOTA_CELL_DEBOUNCE_MS,
	useQuotaCellInputSync,
	draftFromQuotaCellValue,
} from "./useQuotaCellInput";

export default function TableCellAdjustment(params) {
   const dispatch = useDispatch();
   const define = useSelector((state) => state.define);
   const {
      gridData,
      gridDataFiltered,
      selectedDepletionPeriodType,
   } = define;
   const gridDataRef = useRef(gridData);
   const gridDataFilteredRef = useRef(gridDataFiltered);
   gridDataRef.current = gridData;
   gridDataFilteredRef.current = gridDataFiltered;
   const periodTypeRef = useRef(selectedDepletionPeriodType);
   periodTypeRef.current = selectedDepletionPeriodType;
   const cellInput = useQuotaCellInputSync(params.row.adjustment);
   const { draft, setDraft, displayValue, onFocus, onBlur } = cellInput;

   function updateGrid(adjust, updatedQuota, w1, w2, w3, w4, w5) {
      const filtered = gridDataFilteredRef.current;
      const fullGrid = gridDataRef.current;
      const customerID = params.row.customerID;
      const customerGroupID = params.row.customerGroupID;
      const productGroupID = params.row.productGroupID;
      const skuid = params.row.skuid;
      let updatedItem = null;
      const updated = filtered.map((item, index) => {
         const flag =
            item.customerID === customerID &&
            item.customerGroupID === customerGroupID &&
            item.productGroupID === productGroupID &&
            item.skuid === skuid;
         if (flag === true) {
            updatedItem = {
               ...item,
               adjustment: adjust,
               quota: updatedQuota,
               weekly1: !w1 ? 0 : w1,
               weekly2: !w2 ? 0 : w2,
               weekly3: !w3 ? 0 : w3,
               weekly4: !w4 ? 0 : w4,
               weekly5: !w5 ? 0 : w5,
            };
            return updatedItem;
         }
         return item;
      });

      const updatedGrid = [];
      for (let i = 0; i < fullGrid.length; ++i) {
         const item = fullGrid[i];
         if (item.id === updatedItem.id) {
            updatedGrid.push(updatedItem);
         } else {
            updatedGrid.push(item);
         }
      }

      commitGridDataUpdate(dispatch, setState, {
         gridData: updatedGrid,
         gridDataFiltered: updated,
         updatedRow: updatedItem,
         backupFields: PRE_ROUNDER_BACKUP_SYNC_FIELDS,
      });
      dispatch(setTotalValues());
   }

   function updateAdjustmentTemp(adjust) {
      const filtered = gridDataFilteredRef.current;
      const fullGrid = gridDataRef.current;
      const customerID = params.row.customerID;
      const customerGroupID = params.row.customerGroupID;
      const productGroupID = params.row.productGroupID;
      const skuid = params.row.skuid;
      let updatedItem = null;
      const updated = filtered.map((item, index) => {
         const flag =
            item.customerID === customerID &&
            item.customerGroupID === customerGroupID &&
            item.productGroupID === productGroupID &&
            item.skuid === skuid;
         if (flag === true) {
            updatedItem = {
               ...item,
               adjustment: adjust,
            };
            return updatedItem;
         }
         return item;
      });

      const updatedGrid = [];
      for (let i = 0; i < fullGrid.length; ++i) {
         const item = fullGrid[i];
         if (item.id === updatedItem.id) {
            updatedGrid.push(updatedItem);
         } else {
            updatedGrid.push(item);
         }
      }

      commitGridDataUpdate(dispatch, setState, {
         gridData: updatedGrid,
         gridDataFiltered: updated,
         updatedRow: updatedItem,
         backupFields: ["adjustment"],
      });
   }



 function updateAdjustment(event) {
      let adjustStr = removeComma(event.target.value);
      let previousAdj = draft;
      if(adjustStr === "0-") {
         adjustStr = "-";
      }
      let adjust = Number(adjustStr);
      
      if (!isNaN(adjust)) {
         setDraft(adjustStr);
         debouncedUpdate(adjustStr, previousAdj);
      } else if (isNaN(adjust) && adjustStr.length === 1 && adjustStr === "-") {
         setDraft('-');
      } else {
         dispatch(setState("isError", true));
         dispatch(setState("isError_Info", "Only numbers are allowed."));
      }
   }  


	const debouncedUpdate = useMemo(
		() =>
			debounce((adjustStr, previousAdj) => {
				if (adjustStr === "0-") {
					adjustStr = "-";
				}
				let adjust = Number(adjustStr);

				if (!isNaN(adjust)) {
					const row =
						findGridRowByCustomerSku(
							gridDataFilteredRef.current,
							params.row
						) ?? findGridRowByCustomerSku(gridDataRef.current, params.row);
					if (!row) return;

					dispatch(setState("gridDataChanged", true));
					const initialQuota = Number(row.initialQuota);
					const safetyMargin = Number(row.safetyMargin);
					const tender = Number(row.tender);
					let PrevAdjUpd =
						row.adjustment === "-" ? 0 : Number(row.adjustment);
					const val =
						initialQuota === 0 ? 0 : (safetyMargin / 100) * initialQuota;
					const updatedQuota = Math.round(
						initialQuota + val + adjust + tender
					);

					const dkLive = rowDepletionModelKey(row);
					const w1p = Number(row?.weekly1Per ?? 0);
					const w2p = Number(row?.weekly2Per ?? 0);
					const w3p = Number(row?.weekly3Per ?? 0);
					const w4p = Number(row?.weekly4Per ?? 0);
					const w5p = Number(row?.weekly5Per ?? 0);

					if (updatedQuota < 0) {
						dispatch(setState("isError", true));
						dispatch(
							setState("isError_Info", "Quota can not be negative.")
						);
						if (previousAdj === "-") {
							const val0 =
								initialQuota === 0 ? 0 : (safetyMargin / 100) * initialQuota;
							const updatedQuota0 = Math.round(
								initialQuota + val0 + 0 + tender
							);
							if (updatedQuota0 >= 0) {
								setDraft(draftFromQuotaCellValue(0));
								const wcCurveNeg = {
									week1: w1p,
									week2: w2p,
									week3: w3p,
									week4: w4p,
									week5: w5p,
								};
								const bucketsNeg = splitQuotaAcrossWeeks(
									updatedQuota0,
									dkLive,
									wcCurveNeg,
									periodTypeRef.current
								);
								updateGrid(
									0,
									updatedQuota0,
									bucketsNeg.weekly1,
									bucketsNeg.weekly2,
									bucketsNeg.weekly3,
									bucketsNeg.weekly4,
									bucketsNeg.weekly5
								);
							} else {
								setDraft(draftFromQuotaCellValue(PrevAdjUpd));
							}
						} else {
							setDraft(draftFromQuotaCellValue(PrevAdjUpd));
						}
						return;
					}

					const wcCurve = {
						week1: w1p,
						week2: w2p,
						week3: w3p,
						week4: w4p,
						week5: w5p,
					};
					const buckets = splitQuotaAcrossWeeks(
						updatedQuota,
						dkLive,
						wcCurve,
						periodTypeRef.current
					);

					updateGrid(
						adjust,
						updatedQuota,
						buckets.weekly1,
						buckets.weekly2,
						buckets.weekly3,
						buckets.weekly4,
						buckets.weekly5
					);
				}
			}, QUOTA_CELL_DEBOUNCE_MS),
		[
			params.row?.id,
			params.row?.weekly1Per,
			params.row?.weekly2Per,
			params.row?.weekly3Per,
			params.row?.weekly4Per,
			params.row?.weekly5Per,
			params.row?.depletionModel,
			params.row?.customerID,
			params.row?.customerGroupID,
			params.row?.productGroupID,
			params.row?.skuid,
		]
	);

	useEffect(() => {
		return () => debouncedUpdate.cancel();
	}, [debouncedUpdate]);
   
   return (
      <TextField
         variant="standard"
         value={displayValue}
         onChange={updateAdjustment}
         onFocus={onFocus}
         onBlur={() => {
            debouncedUpdate.flush();
            onBlur();
         }}
         sx={{ width: 100 }}
      />
   );
}
